import type { CompareV2DispatchType, CompareV2StateType } from "../redux/store";
import {
    compareSetAvailableGrades,
    compareSetCarData,
    compareSetFilteredCarList,
} from "../redux/actions/NewCarCompareActions";
import type { FetchAvailableGradesType } from "../server/data/fetchAvailableGrades";
import { initCompareV2Settings } from "../redux/actions/CompareV2SettingsActions";
import type { CompareV2SettingsType } from "../redux/reducers/CompareV2SettingsReducer";
import type { SetCarDataType } from "../utils/compareTypes";
import { CompareV2Mode } from "../../../shared-logic/features/compare/utils/types";
import { COMPONENT_COMPARE_V2_PDF } from "../../../shared-logic/server/components";
import type { FetchFnReturnType } from "../../../common-deprecated/utils/backendFetch";
import { NcCompareContext } from "../../../common-deprecated/types/NewCarCompareTypes";
import type { FilteredCarType } from "../../../common-deprecated/types/FilterApiType";

// ----------------------------------------------------------------------
// Fetch types
// ----------------------------------------------------------------------
export const FETCH_AVAILABLE_COMPARE_V2_GRADES = "fetchAvailableCompareV2Grades" as const;
export const FETCH_COMPARE_V2_SETTINGS = "fetchCompareV2Settings" as const;
export const FETCH_COMPARE_V2_PDF = "fetchCompareV2Pdf" as const;
export const FETCH_FILTERED_CAR_LIST = "fetchFilteredCarList" as const;

export type FetchCompareV2GradesType = {
    fetchType: typeof FETCH_AVAILABLE_COMPARE_V2_GRADES;
    ncCompareContext: NcCompareContext;
    result?: FetchFnReturnType<FetchAvailableGradesType | null>;
};

export type FetchCompareV2SettingsType = {
    fetchType: typeof FETCH_COMPARE_V2_SETTINGS;
    compareMode: CompareV2Mode;
    result?: FetchFnReturnType<CompareV2SettingsType>;
};

export type FetchCompareV2PdfType = {
    fetchType: typeof FETCH_COMPARE_V2_PDF;
    result?: FetchFnReturnType<SetCarDataType[] | null>;
};

export type FetchFilteredCarListType = {
    fetchType: typeof FETCH_FILTERED_CAR_LIST;
    apiUrl: string;
    financeProduct?: string;
    result?: FetchFnReturnType<FilteredCarType[]>;
};

// This fetch and it's type shouldn't be exported, see GetComponentFetches for more info
export const fetchAvailableCompareV2Grades = (ncCompareContext: NcCompareContext): FetchCompareV2GradesType => ({
    fetchType: FETCH_AVAILABLE_COMPARE_V2_GRADES,
    ncCompareContext,
});

export const fetchCompareV2Settings = (compareMode: CompareV2Mode): FetchCompareV2SettingsType => ({
    fetchType: FETCH_COMPARE_V2_SETTINGS,
    compareMode,
});

export const fetchCompareNewCarData = (): FetchCompareV2PdfType => ({
    fetchType: FETCH_COMPARE_V2_PDF,
});

export const fetchFilteredCarList = (apiUrl: string, financeProduct?: string): FetchFilteredCarListType => ({
    fetchType: FETCH_FILTERED_CAR_LIST,
    apiUrl,
    financeProduct,
});

export type CompareV2FetchTypes =
    | FetchCompareV2GradesType
    | FetchCompareV2SettingsType
    | FetchCompareV2PdfType
    | FetchFilteredCarListType;

// ----------------------------------------------------------------------
// Fetch getter
// ----------------------------------------------------------------------
export const getCompareV2ComponentFetches = <T extends CompareV2StateType>(state: T): CompareV2FetchTypes[] => {
    const fetches: CompareV2FetchTypes[] = [];
    const { newCarCompare, compareV2Settings, commonSettings } = state;

    if (!compareV2Settings.initialized) {
        fetches.push(fetchCompareV2Settings(CompareV2Mode.New));
        return fetches;
    }

    // TODO What happens if this fails or none are available? It would just keep fetching until it hits the time limit?
    if (!newCarCompare.available.length) {
        fetches.push(fetchAvailableCompareV2Grades(newCarCompare.ncCompareContext));
    }

    if (!newCarCompare.available.length && commonSettings.component === COMPONENT_COMPARE_V2_PDF) {
        fetches.push(fetchCompareNewCarData());
    }

    if (!newCarCompare.filteredCarList?.length && newCarCompare.ncCompareContext === NcCompareContext.Leasing) {
        fetches.push(
            fetchFilteredCarList(
                compareV2Settings.offerFiltersFinanceApi,
                state.newCarCompareExternalData.financeProduct,
            ),
        );
    }

    return fetches;
};

// ----------------------------------------------------------------------
// Dispatcher
// ----------------------------------------------------------------------
export const compareV2FetchDispatcher = (
    data: Required<CompareV2FetchTypes>,
    dispatch: CompareV2DispatchType,
): void => {
    if (data.fetchType === FETCH_AVAILABLE_COMPARE_V2_GRADES) {
        dispatch(compareSetAvailableGrades(data.result.fetchResult));
    }

    if (data.fetchType === FETCH_COMPARE_V2_SETTINGS) {
        dispatch(initCompareV2Settings(data.result.fetchResult));
    }

    if (data.fetchType === FETCH_COMPARE_V2_PDF) {
        data.result.fetchResult?.forEach((compareData: SetCarDataType, index: number) => {
            dispatch(compareSetCarData(compareData, index));
        });
    }

    if (data.fetchType === FETCH_FILTERED_CAR_LIST) {
        dispatch(compareSetFilteredCarList(data.result.fetchResult));
    }
};
